import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DiscoverApp } from "@majourneys/discover";
import { majourneysDiscoverConfig } from "../../../features/discover/config";
import { PageSeo, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { BreadcrumbLd, siteUrl } from "../../../shared/seo/breadcrumb-ld";

/**
 * `?utility=` was how the four sections were addressed before each got a path.
 * Those links are out in the world, so they keep working — as a 301 onto the
 * path, never the default 307, so the signal consolidates rather than sitting
 * on two URLs.
 */
const LEGACY_UTILITY: Record<string, string> = {
  chat: "/$lang/ai-chat",
  trips: "/$lang/trips",
  liked: "/$lang/liked",
};

export const Route = createFileRoute("/$lang/_layout/discover")({
  component: DiscoverRouteComponent,
  validateSearch: (search: Record<string, unknown>): { utility?: string; item?: string } => ({
    utility: typeof search.utility === "string" ? search.utility : undefined,
    item: typeof search.item === "string" ? search.item : undefined,
  }),
  beforeLoad: ({ params, search }) => {
    const to = LEGACY_UTILITY[(search as { utility?: string }).utility ?? ""];
    if (!to) return;
    throw redirect({
      to,
      params: { lang: (params as { lang?: string }).lang ?? "en" },
      statusCode: 301,
      replace: true,
    });
  },
});

function DiscoverRouteComponent() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = params.lang ?? "en";
  const title = t("discover.seo_title");
  const description = t("discover.seo_description");
  const h1 = t("discover.h1", { defaultValue: "Discover Morocco" });
  const canonical = `${SITE_ORIGIN}/${lang}/discover`;
  const collectionLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${canonical}#collection`,
    name: title,
    description,
    url: canonical,
    inLanguage: lang,
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    about: { "@type": "Country", name: "Morocco" },
  };
  return (
    <>
      <PageSeo
        title={title}
        description={description}
        canonicalPath={`/${lang}/discover`}
        lang={lang}
        ogType="website"
        jsonLd={[collectionLd]}
      />
      <BreadcrumbLd
        items={[
          { name: t("nav.home"), url: siteUrl(lang) },
          { name: h1 },
        ]}
      />
      <h1 className="sr-only">{h1}</h1>
      <DiscoverApp config={majourneysDiscoverConfig} />
    </>
  );
}
